import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import CopyableValue from "components/CopyableValue";
import { formatDate } from "components/helpers";
import { CampaignList } from "components/sidebar/sections/EntityCampaignsPanel";
import EntitySection from "components/sidebar/sections/EntitySection";
import {
  CustomFieldsPanelSection,
  PanelField,
  PanelSection,
} from "components/sidebar/sections/InfoPanel";
import { PanelSectionSkeleton } from "components/sidebar/sections/PanelSectionSkeleton";
import TagsPanelSection from "components/sidebar/sections/TagsPanelSection";
import { CASE_TIERS } from "constants/Case";
import { formatAddress } from "helpers/address";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import CaseService from "services/Case";

const caseTierName = tier =>
  Object.keys(CASE_TIERS).find(key => CASE_TIERS[key] === tier);

const CaseCampaigns = ({ caseId, isRecordLoading }) => {
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["case", caseId, "campaigns"],
    queryFn: () => CaseService.getCampaigns(caseId),
    enabled: !!caseId,
  });

  if (isRecordLoading || isLoading) {
    return <PanelSectionSkeleton />;
  }

  if (campaigns.length === 0) {
    return null;
  }

  return (
    <PanelSection title="Campaigns">
      <CampaignList
        campaigns={campaigns}
        buildTo={({ campaign_id }) => `/campaigns/${campaign_id}/cases`}
      />
    </PanelSection>
  );
};

// Read-only information for the case detail rail; controls live in the header.
// Tag lists stay editable — the tag dropdown in each section title is the only
// affordance, and the user's "Show disabled tags" preference governs which tags
// they show.
const CaseInfoPanel = ({
  caseData,
  entity,
  showDisabledTags,
  onUpdate,
  isLoading = false,
  isEntityLoading = false,
}) => {
  const { enable_aegis_automation_status } = useRuvixxFlags();

  return (
    <>
      <PanelSection isLoading={isLoading}>
        <PanelField label="Case ID">{caseData?.id}</PanelField>
        <PanelField label="External Case ID">{caseData?.case_id}</PanelField>
        <PanelField label="Entity">
          {caseData?.entity ? (
            <Link
              to={`/entities/${caseData.entity_id}`}
              className="tw:text-sidebar-foreground! tw:hover:underline"
            >
              {caseData.entity.name}
            </Link>
          ) : null}
        </PanelField>
        <PanelField label="Case Tier">
          {caseTierName(caseData?.case_tier)}
        </PanelField>
        <PanelField label="Investigation Status">
          {caseData?.investigation_status?.name}
        </PanelField>
        <PanelField label="Case Investigators">
          {(caseData?.case_investigators ?? []).map(({ name }) => name)}
        </PanelField>
        <PanelField label="Website">{caseData?.website}</PanelField>
        <PanelField label="Industry">{caseData?.industry}</PanelField>
        <PanelField label="Company Description">
          {caseData?.company_description}
        </PanelField>
        <PanelField label="Addresses">
          {caseData?.addresses?.map(address => (
            <CopyableValue key={address.id} value={formatAddress(address)} />
          ))}
        </PanelField>
        <PanelField label="Actionable Domain">
          {caseData?.actionable_domain}
        </PanelField>
        <PanelField label="Region">{caseData?.region_name}</PanelField>
        <PanelField label="Country">{caseData?.country}</PanelField>
        <PanelField label="Source Status">
          {caseData?.source_status_name}
        </PanelField>
        <PanelField label="Product Line">
          {caseData?.product_line_name}
        </PanelField>
      </PanelSection>

      <TagsPanelSection
        title="Case Tags"
        modelType="Case"
        record={caseData}
        isLoading={isLoading}
        showDisabledTags={showDisabledTags}
        onUpdate={onUpdate}
      />

      <CustomFieldsPanelSection
        title="Custom Fields"
        items={caseData?.custom_fields}
        modelType="Case"
        isLoading={isLoading}
      />

      {(isLoading || caseData?.id) && (
        <CaseCampaigns caseId={caseData?.id} isRecordLoading={isLoading} />
      )}

      <PanelSection title="Case Summary" isLoading={isLoading}>
        <PanelField label="Number of Machines">
          {caseData?.total_num_machines}
        </PanelField>
        <PanelField label="Number of Recent Machines">
          {caseData?.recent_num_machines}
        </PanelField>
        <PanelField label="Number of Approved Machines">
          {caseData?.approved_num_machines}
        </PanelField>
        <PanelField label="Data Score">{caseData?.data_score}</PanelField>
        <PanelField label="First Event">
          {formatDate(caseData?.first_event)}
        </PanelField>
        <PanelField label="Last Event">
          {formatDate(caseData?.last_event)}
        </PanelField>
        <PanelField label="Time Span">{caseData?.time_span}</PanelField>
        <PanelField label="Created">
          {formatDate(caseData?.created_at)}
        </PanelField>
        <PanelField label="Updated">
          {formatDate(caseData?.updated_at)}
        </PanelField>
        <PanelField label="Refreshed At">
          {formatDate(caseData?.refreshed_at)}
        </PanelField>
        <PanelField label="Automation Status">
          {caseData?.automation_status}
        </PanelField>
        {enable_aegis_automation_status && (
          <PanelField label="Aegis Automation Status">
            {caseData?.aegis_automation_status?.name}
          </PanelField>
        )}
      </PanelSection>

      {(isEntityLoading || entity?.id) && (
        <EntitySection
          entity={entity}
          isLoading={isEntityLoading}
          showDisabledTags={showDisabledTags}
          onUpdate={onUpdate}
          showAddresses
          showEdit
        />
      )}
    </>
  );
};

export default CaseInfoPanel;
